import '../Profile.scss';

import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';

import { addService, editService, loadProvider } from 'actions/provider';
import Dropdown from 'atoms/dropdown';
import Input from 'atoms/input';
import ModalContent from 'atoms/modal-content';
import toast from 'atoms/toast';
import { durationOptions } from 'util/constants';

import DeleteService from './DeleteService';

function EditServiceModal({ isOpen, openModal, service }) {
  const dispatch = useDispatch();
  const [showDeleteModal, openDeleteModal] = useState(false);
  const currentLocation = useSelector((state) => state.provider.locations)[0];
  const typeId = currentLocation?.type[0].id;
  const { provider, core } = useSelector((state) => state.main);
  const { tag } = provider.service;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [animalTypeId, setAnimalType] = useState('');
  const [sizeId, setSize] = useState('');
  const [durationMinutes, setDuration] = useState('');
  const [amount, setAmount] = useState('');
  const [animalAgeId, setAge] = useState('');
  const [breedId, setBreed] = useState('');
  const [hairLengthId, setHairLength] = useState('');
  const [tags, setTags] = useState({
    0: {},
    1: {},
    2: {},
    3: {},
    4: {},
  });
  useEffect(() => {
    if (service) {
      setName(service.name);
      setDescription(service.description);
      setDuration({
        value: `${service.durationMinutes} min`,
        id: service.durationMinutes,
      });
      setAmount(service.price?.amount);
      setAnimalType(service.animal?.type);
      setSize(service.size);
      setAge(service.animal?.age);
      setBreed(service.breed);
      setHairLength(service.hair?.length);
      setTags(service.tags);
    }
  }, [service]);

  const onUpdate = () => {
    const body = {
      name,
      description,
      durationMinutes: durationMinutes?.id,
      amount,
      animalAgeId: animalAgeId?.id || null,
      animalTypeId: animalTypeId?.id,
      breedId: breedId?.id,
      hairLengthId: hairLengthId?.id,
      sizeId: sizeId?.id,
      tagIds: Object.entries(tags)
        .map(([key, data]) => data?.id)
        .filter(Boolean),
      typeId,
    };
    let action = editService;
    if (!service) {
      action = addService;
    }
    dispatch(action(body, service?.uuid)).then(({ success }) => {
      if (success) {
        dispatch(loadProvider()).then(() => toast({ text: 'Service saved!' }));
      }
    });
  };

  const onDelete = () => {
    openDeleteModal(true);
  };

  return (
    <>
      <Modal
        ariaHideApp={false}
        isOpen={isOpen}
        onRequestClose={() => openModal(false)}
        shouldCloseOnOverlayClick
        className="edit-info-modal"
      >
        <ModalContent
          title={`${!service ? 'Add' : 'Edit'} service`}
          primaryButton="Update"
          secondaryButton="Delete"
          isButtonDisabled={
            !amount || !name || !description || !durationMinutes
          }
          primaryButtonAction={onUpdate}
          secondaryButtonAction={onDelete}
          onClose={() => openModal(false)}
        >
          <Input
            id="name"
            label="Service"
            placeholder="Service title"
            onChange={(value) => setName(value)}
            value={name}
          />
          <Input
            id="description"
            label="Description"
            placeholder="Describe the service"
            inputType="textarea"
            className="textarea mt-30"
            onChange={(value) => setDescription(value)}
            value={description}
          />
          <Dropdown
            id="type"
            label="What type of animal is this for?"
            className="mt-30"
            placeholder="Type"
            items={core.type}
            onClick={(value) => {
              setAnimalType(value);
              setAge('');
              setBreed('');
              setSize('');
              setHairLength('');
            }}
            value={animalTypeId}
          />
          <p className="label mt-35 mb-10">Tags (up to 5)</p>
          <div className="flex-row tags-container">
            <div className="flex-row w-100">
              {[0, 1, 2].map((item) => (
                <Dropdown
                  key={item}
                  id={`tag-${item}`}
                  className="hide-label mr-10"
                  placeholder={`Tag ${item + 1}`}
                  items={tag}
                  onClick={(value) =>
                    setTags((prev) => ({ ...prev, [item]: value }))
                  }
                  disabledValues={Object.entries(tags)
                    .map(([key, data]) => data?.value)
                    .filter(Boolean)}
                  isClearable
                  value={tags[item]}
                />
              ))}
            </div>
            <div className="flex-row w-100">
              {[3, 4].map((item) => (
                <Dropdown
                  key={item}
                  id={`tag-${item}`}
                  className="hide-label mr-10"
                  placeholder={`Tag ${item + 1}`}
                  items={tag}
                  onClick={(value) =>
                    setTags((prev) => ({ ...prev, [item]: value }))
                  }
                  disabledValues={Object.entries(tags)
                    .map(([key, data]) => data?.value)
                    .filter(Boolean)}
                  isClearable
                  value={tags[item]}
                />
              ))}
              <div className="input hidden" />
            </div>
          </div>
          {animalTypeId?.id < 3 && (
            <>
              <p className="label mt-35 mb-10">Additional details (optional)</p>
              <div className="flex-row align-end">
                <Dropdown
                  placeholder="Size"
                  className="w-100 mr-10 hide-label"
                  items={core.size}
                  isClearable
                  onClick={(value) => setSize(value)}
                  value={sizeId}
                />
                <Dropdown
                  placeholder="Hair length"
                  className="w-100 hide-label"
                  items={core.hair.length.filter(
                    (el) => el.animalTypeId === animalTypeId?.id
                  )}
                  isClearable
                  onClick={(value) => setHairLength(value)}
                  value={hairLengthId}
                />
              </div>
              <div className="flex-row align-end">
                <Dropdown
                  placeholder={
                    animalTypeId?.id === 2 ? 'Puppy, adult' : 'Kitten, adult'
                  }
                  className="w-100 hide-label mr-10"
                  items={core.age.filter(
                    (el) => el.animalTypeId === animalTypeId?.id
                  )}
                  isClearable
                  onClick={(value) => setAge(value)}
                  value={animalAgeId}
                />
                <Dropdown
                  placeholder="Breed"
                  className="w-100 hide-label"
                  items={core.breed.filter(
                    (el) => el.animalTypeId === animalTypeId?.id
                  )}
                  isClearable
                  onClick={(value) => setBreed(value)}
                  value={breedId}
                />
              </div>
            </>
          )}
          <div className="flex-row align-end mt-25">
            <Dropdown
              id="cost"
              label="Time and cost"
              className="w-100 hide-label mr-10"
              placeholder="60 minutes"
              items={durationOptions}
              onClick={(value) => setDuration(value)}
              value={durationMinutes}
            />
            <Input
              id="amount"
              placeholder="$150"
              className="w-100"
              isMoney
              onChange={(value) => setAmount(value)}
              value={amount}
            />
          </div>
        </ModalContent>
      </Modal>
      {showDeleteModal && (
        <DeleteService
          serviceId={service.uuid}
          isOpen={showDeleteModal}
          openModal={openDeleteModal}
          closeServiceModal={() => openModal(false)}
        />
      )}
    </>
  );
}

export default EditServiceModal;
